import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import className from 'classnames';
import { LegTypeProps, LocationProp } from '../../models';
import { FragmentBody, FieldHeaderPrisma } from '../FlightsManager/MobileFragments';
import { LocationUtils, withPageContext } from '../../utils';
import { ConnectedAirportSelectorDropdown } from './AirportSelectorDropdown';
import { ConnectedAirportSelectorInputMobile } from './AirportSelectorInputMobile';

export const AirportSelectorPrisma = ({
    activeProduct,
    title,
    setValue,
    sources,
    value,
    focus,
    fetchedLocations,
    departureOrArrival,
    errorMessage,
    trackEvent,
}) => {
    const initialStateWord = LocationUtils.getInputText(value);
    const [displayedWord, setDisplayedWord] = useState(initialStateWord);
    const [ownFocus, setOwnFocus] = useState(focus);
    const airportSelectorRef = useRef();
    const airportInputRef = useRef();

    const classNames = className({
        'prisma-input-group': true,
        'focused': ownFocus,
        'valid': !ownFocus && displayedWord.length > 0 && errorMessage,
        'invalid': !ownFocus && displayedWord.length > 0 && !!errorMessage,
    });

    const handleClickOutside = useCallback(() => {
        setOwnFocus(false);
        if (!value) {
            setDisplayedWord('');
            setValue(undefined);
        }
    }, [value, setOwnFocus, setDisplayedWord, setValue]);

    const handleClick = useCallback((event) => {
        if (
            !airportInputRef.current?.contains(event.target) &&
            !airportSelectorRef.current?.contains(event.target) &&
            ownFocus
        ) {
            handleClickOutside();
        }
    }, [ownFocus, airportInputRef, airportSelectorRef, handleClickOutside]);

    const onClickItem = (value) => {
        setDisplayedWord(LocationUtils.getInputText(value));
        setOwnFocus(false);
        setValue(value);
    };

    const onChangeInput = (text) => {
        setDisplayedWord(text);
        setValue(undefined);
    };

    const onFocusInput = () => setOwnFocus(true);

    useEffect(() => {
        setOwnFocus(focus);
    }, [focus]);

    useEffect(() => {
        document.addEventListener('touchend', handleClick);
        return () => document.removeEventListener('touchend', handleClick);
    }, [ownFocus, displayedWord, fetchedLocations, handleClick]);

    return (
        <>
            <FieldHeaderPrisma
                textKey={title}
                hasLocationInput
                activeProduct={activeProduct}
                departureOrArrival={departureOrArrival}
                trackEvent={trackEvent}
            >
                <div ref={airportInputRef} className={classNames}>
                    <ConnectedAirportSelectorInputMobile
                        focus={ownFocus}
                        departureOrArrival={departureOrArrival}
                        displayedWord={displayedWord}
                        onChangeInput={onChangeInput}
                        onFocusInput={onFocusInput}
                        errorMessage={errorMessage}
                    />
                </div>
            </FieldHeaderPrisma>
            <FragmentBody padding={false}>
                <div ref={airportSelectorRef}>
                    <ConnectedAirportSelectorDropdown
                        activeProduct={activeProduct}
                        displayedWord={displayedWord}
                        hasSelectedValue={!!value}
                        focus={ownFocus}
                        onClickItem={onClickItem}
                        sources={sources}
                        isFixedWidth
                        hasBoxStyle={false}
                        departureOrArrival={departureOrArrival}
                    />
                </div>
            </FragmentBody>
        </>
    );
};

AirportSelectorPrisma.propTypes = {
    fetchedLocations: PropTypes.object,
    title: PropTypes.string.isRequired,
    setValue: PropTypes.func.isRequired,
    sources: PropTypes.array.isRequired,
    value: LocationProp,
    focus: PropTypes.bool,
    departureOrArrival: LegTypeProps.isRequired,
    errorMessage: PropTypes.string,
    activeProduct: PropTypes.string,
    trackEvent: PropTypes.func,
};

AirportSelectorPrisma.defaultProps = {
    fetchedLocations: {},
    value: null,
    focus: false,
    errorMessage: '',
    activeProduct: null,
    trackEvent: () => {},
};

const mapStateToProps = ({ locationAutocomplete, activeProduct }) => ({
    fetchedLocations: locationAutocomplete.locations,
    activeProduct: activeProduct.activeProduct,
});

const connected = connect(mapStateToProps, {})(withPageContext(AirportSelectorPrisma));
export { connected as ConnectedAirportSelectorPrisma };
